import { useMemo, useState } from "react";
import VehicleFormModal from "@/pages/admin/customers/components/VehicleFormModal";
import { useWorkshopData } from "@/hooks/useWorkshop";
import { supabase } from "@/lib/supabase";
import { logAudit } from "@/lib/audit";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import type { CustomerVehicle } from "@/types";

export default function AdminVehiclesPage() {
  const toast = useToast();
  const { profile } = useAuth();
  const { vehicles, customers, loading, error, reload } = useWorkshopData();

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<CustomerVehicle | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return vehicles.filter((vehicle) => {
      if (typeFilter && vehicle.vehicle_type !== typeFilter) return false;
      if (!term) return true;
      const haystack = `${vehicle.brand ?? ""} ${vehicle.model ?? ""} ${vehicle.plate ?? ""} ${
        vehicle.vin ?? ""
      } ${vehicle.customer_name ?? ""}`.toLowerCase();
      return haystack.includes(term);
    });
  }, [vehicles, search, typeFilter]);

  const stats = useMemo(() => {
    const total = vehicles.length;
    const withPlate = vehicles.filter((v) => v.plate).length;
    const customersWithVehicles = new Set(vehicles.map((v) => v.customer_id)).size;
    return { total, withPlate, customersWithVehicles };
  }, [vehicles]);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (vehicle: CustomerVehicle) => {
    setEditing(vehicle);
    setFormOpen(true);
  };

  const handleDelete = async (vehicle: CustomerVehicle) => {
    setDeletingId(vehicle.id);
    const { error: deleteError } = await supabase
      .from("customer_vehicles")
      .delete()
      .eq("id", vehicle.id);
    setDeletingId(null);
    if (deleteError) {
      toast.error("No se pudo eliminar el vehículo.");
      return;
    }
    await logAudit({
      userId: profile?.id ?? null,
      userName: `${profile?.full_name ?? ""} ${profile?.last_name ?? ""}`.trim() || "Usuario",
      action: "eliminar",
      entity: "customer_vehicles",
      entityId: vehicle.id,
      branchId: profile?.branch_id ?? null,
      oldValue: { placa: vehicle.plate, marca: vehicle.brand },
    });
    toast.success("Vehículo eliminado.");
    reload();
  };

  const statCards = [
    { key: "total", label: "Vehículos", value: stats.total, icon: "ri-truck-line", tone: "primary" },
    { key: "plate", label: "Con placa", value: stats.withPlate, icon: "ri-roadster-line", tone: "accent" },
    {
      key: "customers",
      label: "Clientes con vehículos",
      value: stats.customersWithVehicles,
      icon: "ri-user-3-line",
      tone: "secondary",
    },
  ];

  const toneClass: Record<string, string> = {
    primary: "bg-primary-100 text-primary-700",
    accent: "bg-accent-100 text-accent-800",
    secondary: "bg-secondary-200 text-secondary-900",
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950">Taller y Vehículos</h1>
          <p className="mt-1.5 text-sm text-foreground-500">
            Vehículos asociados a cada cliente. Sirven para dar seguimiento a los servicios del taller.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-add-line text-base"></i>
          Nuevo vehículo
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {statCards.map((card) => (
          <div
            key={card.key}
            className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-3"
          >
            <span className={`flex h-9 w-9 items-center justify-center rounded-md ${toneClass[card.tone]}`}>
              <i className={`${card.icon} text-base`}></i>
            </span>
            <div>
              <p className="font-heading text-lg font-bold leading-none text-foreground-950">{card.value}</p>
              <p className="mt-1 text-[11px] text-foreground-500">{card.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100 p-4 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <span className="pointer-events-none absolute left-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-search-line text-base"></i>
          </span>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar por placa, marca, modelo, chasis o cliente..."
            className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <select
          value={typeFilter}
          onChange={(event) => setTypeFilter(event.target.value)}
          className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
        >
          <option value="">Todos los tipos</option>
          {Array.from(new Set(vehicles.map((v) => v.vehicle_type).filter(Boolean))).map((type) => (
            <option key={type as string} value={type as string}>
              {type}
            </option>
          ))}
        </select>
      </div>

      {error ? (
        <div className="flex flex-col items-start gap-3 rounded-lg border border-primary-200 bg-primary-50 p-4 text-sm text-primary-800 sm:flex-row sm:items-center sm:justify-between">
          <span className="flex items-center gap-2">
            <i className="ri-error-warning-line text-base"></i>
            {error}
          </span>
          <button
            type="button"
            onClick={reload}
            className="whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-3 py-1.5 text-xs font-semibold text-primary-700"
          >
            Reintentar
          </button>
        </div>
      ) : null}

      {loading ? (
        <div className="flex items-center justify-center gap-3 rounded-lg border border-background-200 bg-background-100 py-14 text-sm text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-lg"></i>
          Cargando vehículos...
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-background-300 bg-background-100 px-4 py-14 text-center">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-truck-line text-2xl"></i>
          </span>
          <p className="text-sm font-semibold text-foreground-800">Sin vehículos</p>
          <p className="max-w-sm text-xs text-foreground-500">
            Registra un vehículo y asígnalo a un cliente para llevar el historial del taller.
          </p>
        </div>
      ) : (
        <>
          <div className="hidden overflow-hidden rounded-lg border border-background-200 lg:block">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-background-100 text-left text-[11px] font-semibold uppercase tracking-wide text-foreground-500">
                  <th className="px-4 py-3">Vehículo</th>
                  <th className="px-4 py-3">Cliente</th>
                  <th className="px-4 py-3">Placa</th>
                  <th className="px-4 py-3">Tipo</th>
                  <th className="px-4 py-3 text-right">Kilometraje</th>
                  <th className="px-4 py-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200 bg-background-50">
                {filtered.map((vehicle) => (
                  <tr key={vehicle.id} className="text-sm text-foreground-800">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                          <i className="ri-truck-line text-base"></i>
                        </span>
                        <div className="min-w-0">
                          <p className="truncate font-semibold text-foreground-950">
                            {[vehicle.brand, vehicle.model].filter(Boolean).join(" ") || "Vehículo"}
                          </p>
                          <p className="truncate text-[11px] text-foreground-400">
                            {vehicle.year ? `Año ${vehicle.year}` : "Año no definido"}
                            {vehicle.color ? ` · ${vehicle.color}` : ""}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-xs font-medium text-foreground-700">{vehicle.customer_name}</p>
                      <p className="text-[11px] text-foreground-400">{vehicle.customer_phone || "Sin teléfono"}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex rounded-md border border-background-300 bg-background-100 px-2.5 py-1 text-xs font-semibold text-foreground-800">
                        {vehicle.plate || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-foreground-600">{vehicle.vehicle_type || "—"}</td>
                    <td className="px-4 py-3 text-right text-foreground-600">
                      {vehicle.mileage != null ? `${vehicle.mileage} km` : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => openEdit(vehicle)}
                          className="flex h-8 w-8 items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-600 transition-colors hover:bg-background-100"
                        >
                          <i className="ri-edit-2-line text-sm"></i>
                        </button>
                        <button
                          type="button"
                          disabled={deletingId === vehicle.id}
                          onClick={() => handleDelete(vehicle)}
                          className="flex h-8 w-8 items-center justify-center rounded-md border border-primary-200 bg-primary-50 text-primary-700 transition-colors hover:bg-primary-100 disabled:opacity-60"
                        >
                          <i className="ri-delete-bin-6-line text-sm"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:hidden">
            {filtered.map((vehicle) => (
              <div
                key={vehicle.id}
                className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100 p-4"
              >
                <div className="flex items-start gap-3">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-secondary-100 text-secondary-900">
                    <i className="ri-truck-line text-xl"></i>
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-foreground-950">
                      {[vehicle.brand, vehicle.model].filter(Boolean).join(" ") || "Vehículo"}
                    </p>
                    <p className="truncate text-[11px] text-foreground-400">
                      {vehicle.plate ? `Placa ${vehicle.plate}` : "Sin placa"} · {vehicle.customer_name}
                    </p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[10px] font-semibold text-secondary-900">
                        {vehicle.vehicle_type || "Vehículo"}
                      </span>
                      {vehicle.year ? (
                        <span className="rounded-full bg-background-200 px-2 py-0.5 text-[10px] font-semibold text-foreground-700">
                          {vehicle.year}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2 border-t border-background-200 pt-3">
                  <button
                    type="button"
                    onClick={() => openEdit(vehicle)}
                    className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-600"
                  >
                    <i className="ri-edit-2-line text-base"></i>
                  </button>
                  <button
                    type="button"
                    disabled={deletingId === vehicle.id}
                    onClick={() => handleDelete(vehicle)}
                    className="flex h-9 w-9 items-center justify-center rounded-md border border-primary-200 bg-primary-50 text-primary-700 disabled:opacity-60"
                  >
                    <i className="ri-delete-bin-6-line text-base"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <VehicleFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={reload}
        vehicle={editing}
        customers={customers}
      />
    </div>
  );
}